import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/base/SEOHead';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://personalinjury-junlawfirm.com';

const wrongfulDeathSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/wrongful-death#webpage`,
      url: `${siteUrl}/wrongful-death`,
      name: 'Wrongful Death Attorney Indiana | Fishers Wrongful Death Lawyer | Jun Law Firm',
      description:
        'Lost a loved one due to negligence in Indiana? Jun Law Firm provides compassionate legal representation for immigrant families seeking wrongful death justice. Free consultation. (317) 701-2768.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        '@id': `${siteUrl}/wrongful-death#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Wrongful Death', item: `${siteUrl}/wrongful-death` },
        ],
      },
    },
    {
      '@type': 'LegalService',
      '@id': `${siteUrl}/wrongful-death#service`,
      name: 'Jun Law Firm - Wrongful Death Attorney Indiana',
      url: `${siteUrl}/wrongful-death`,
      telephone: '+13177012768',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fishers',
        addressRegion: 'IN',
        postalCode: '46037',
        addressCountry: 'US',
      },
      areaServed: { '@type': 'State', name: 'Indiana' },
      description:
        'Compassionate wrongful death attorney in Indiana representing immigrant families after losing a loved one due to negligence. Car accidents, workplace fatalities, medical malpractice. Free consultation.',
      priceRange: 'Free consultation',
      availableLanguage: [
        { '@type': 'Language', name: 'English' },
        { '@type': 'Language', name: 'Spanish' },
        { '@type': 'Language', name: 'Korean' },
        { '@type': 'Language', name: 'Chinese' },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Who can file a wrongful death lawsuit in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'In Indiana, a wrongful death claim is filed by the personal representative (executor or administrator) of the deceased person\'s estate on behalf of surviving family members, including the spouse, children, and other dependents.',
          },
        },
        {
          '@type': 'Question',
          name: 'What damages can be recovered in a wrongful death case in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Surviving families may recover economic damages such as funeral expenses, medical bills, lost income, and loss of future earnings, as well as non-economic damages including loss of companionship, guidance, love, and mental anguish.',
          },
        },
        {
          '@type': 'Question',
          name: 'How long do I have to file a wrongful death claim in Indiana?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Indiana wrongful death cases generally must be filed within 2 years of the date of death. It is critical to contact an attorney as soon as possible to preserve evidence and protect your family\'s rights.',
          },
        },
      ],
    },
  ],
};

export default function WrongfulDeath() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Add scroll animation
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('section');
    sections.forEach((section) => {
      section.classList.add('opacity-0');
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const commonCauses = [
    {
      number: 'I',
      title: 'Fatal Car Accidents',
      description: 'Drunk driving, distracted driving, reckless driving, and other negligent behaviors causing fatal collisions.'
    },
    {
      number: 'II',
      title: 'Workplace Fatalities',
      description: 'Construction accidents, industrial incidents, and unsafe working conditions leading to worker deaths.'
    },
    {
      number: 'III',
      title: 'Medical Malpractice',
      description: 'Surgical errors, misdiagnosis, medication mistakes, and other medical negligence causing death.'
    },
    {
      number: 'IV',
      title: 'Defective Products',
      description: 'Dangerous drugs, faulty machinery, defective vehicles, and other products causing fatal injuries.'
    },
    {
      number: 'V',
      title: 'Premises Liability',
      description: 'Fatal falls, drownings, fires, and other deaths caused by unsafe property conditions.'
    },
    {
      number: 'VI',
      title: 'Nursing Home Abuse',
      description: 'Neglect, abuse, and substandard care in nursing homes and assisted living facilities.'
    }
  ];

  const compensationTypes = [
    {
      category: 'Economic Damages',
      items: [
        'Funeral and burial expenses',
        'Medical bills before death',
        'Lost income and benefits',
        'Loss of future earnings',
        'Loss of inheritance'
      ]
    },
    {
      category: 'Non-Economic Damages',
      items: [
        'Loss of companionship',
        'Loss of guidance and counsel',
        'Loss of love and affection',
        'Mental anguish and grief',
        'Loss of consortium'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-lavender-50">
      <SEOHead
        title="Wrongful Death Attorney Indiana | Fishers Wrongful Death Lawyer | Jun Law Firm"
        description="Lost a loved one due to negligence in Indiana? Jun Law Firm provides compassionate legal representation for immigrant families seeking wrongful death justice. Free consultation. (317) 701-2768."
        keywords="wrongful death attorney Indiana, wrongful death lawyer Fishers, fatal car accident attorney Indiana, negligence death lawyer, immigrant wrongful death attorney"
        canonicalPath="/wrongful-death"
        schema={wrongfulDeathSchema}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="text-xs tracking-widest text-gray-500 mb-6 uppercase font-light" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Wrongful Death Law
            </div>
            <h1 className="text-7xl md:text-8xl font-light text-gray-900 mb-8 leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>
              Wrongful Death <em className="font-light italic">Attorney</em>
            </h1>
            <div className="w-24 h-px bg-gray-900 mb-8"></div>
            <p className="text-xl text-gray-700 mb-12 leading-relaxed max-w-2xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Lost a loved one due to someone else's negligence in Indiana? Jun Law Firm provides compassionate legal representation to immigrant families seeking justice and compensation after a wrongful death. We understand your grief and fight to hold negligent parties accountable.
            </p>
            <div className="flex gap-6">
              <button 
                onClick={() => {
                  const element = document.getElementById('contact-section');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-gray-900 text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-800 transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
              >
                Free Consultation
              </button>
              <a 
                href="tel:+13177012768"
                className="border-2 border-gray-900 text-gray-900 px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-900 hover:text-white transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
              >
                Call: (317) 701-2768
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Common Causes */}
      <section className="py-24 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Legal Grounds
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Common Causes of <em className="italic">Wrongful Death</em>
            </h2>
            <div className="w-16 h-px bg-gray-900 mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              When negligence, recklessness, or intentional misconduct causes a death, surviving family members have the right to seek justice and compensation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            {commonCauses.map((cause, index) => (
              <article key={index} className="group cursor-pointer">
                <div className="text-6xl font-light text-gray-300 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {cause.number}
                </div>
                <h3 className="text-2xl font-light text-gray-900 mb-3 group-hover:text-gray-600 transition-colors" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  {cause.title}
                </h3>
                <div className="w-12 h-px bg-gray-300 mb-4"></div>
                <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {cause.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Compensation */}
      <section className="py-24 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
              Legal Recovery
            </div>
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Compensation for <em className="italic">Surviving Families</em>
            </h2>
            <div className="w-16 h-px bg-gray-900 mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Indiana wrongful death law allows surviving family members to recover damages for both economic losses and emotional suffering.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 mb-20">
            {compensationTypes.map((type, index) => (
              <article key={index} className="border-l-2 border-gray-900 pl-8 hover:border-gray-500 transition-colors">
                <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                  {String(index + 1).padStart(2, '0')}
                </div>
                <h3 className="text-2xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  {type.category}
                </h3>
                <ul className="space-y-3">
                  {type.items.map((item, idx) => (
                    <li key={idx} className="text-sm text-gray-600" style={{ fontFamily: "'Inter', sans-serif" }}>
                      — {item}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className="bg-gray-50 border-l-4 border-gray-900 p-12">
            <div className="text-xs tracking-widest text-gray-500 mb-4 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
              Legal Standing
            </div>
            <h3 className="text-3xl font-light text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Who Can File a <em className="italic">Wrongful Death Claim</em>
            </h3>
            <p className="text-gray-700 mb-8 leading-relaxed" style={{ fontFamily: "'Crimson Pro', serif" }}>
              Indiana law specifies who has the legal right to file a wrongful death lawsuit. Understanding these rules is important for immigrant families navigating the legal system.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                  01
                </div>
                <h4 className="text-lg font-light text-gray-900 mb-2" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  Personal Representative
                </h4>
                <div className="w-8 h-px bg-gray-300 mx-auto mb-3"></div>
                <p className="text-sm text-gray-600" style={{ fontFamily: "'Inter', sans-serif" }}>
                  The executor or administrator of the deceased person's estate files the claim on behalf of survivors.
                </p>
              </div>
              <div className="text-center">
                <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                  02
                </div>
                <h4 className="text-lg font-light text-gray-900 mb-2" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  Surviving Spouse
                </h4>
                <div className="w-8 h-px bg-gray-300 mx-auto mb-3"></div>
                <p className="text-sm text-gray-600" style={{ fontFamily: "'Inter', sans-serif" }}>
                  The surviving spouse is entitled to compensation for loss of companionship and financial support.
                </p>
              </div>
              <div className="text-center">
                <div className="text-xs tracking-widest text-gray-500 mb-3 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.15em' }}>
                  03
                </div>
                <h4 className="text-lg font-light text-gray-900 mb-2" style={{ fontFamily: "'Crimson Pro', serif" }}>
                  Children & Dependents
                </h4>
                <div className="w-8 h-px bg-gray-300 mx-auto mb-3"></div>
                <p className="text-sm text-gray-600" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Children and other dependents can recover damages for loss of support and guidance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contact-section" className="py-24 bg-gray-900 text-white border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-xs tracking-widest text-gray-400 mb-6 uppercase" style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.2em' }}>
            Seek Justice
          </div>
          <h2 className="text-5xl md:text-6xl font-light mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            Honor Your <em className="italic">Loved One</em>
          </h2>
          <div className="w-16 h-px bg-white mx-auto mb-8"></div>
          <p className="text-xl text-gray-300 mb-12 leading-relaxed" style={{ fontFamily: "'Crimson Pro', serif" }}>
            No amount of money can replace your loved one, but holding negligent parties accountable can provide closure and financial security for your family. Contact Jun Law Firm today.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="tel:+13177012768"
              className="bg-white text-gray-900 px-10 py-4 text-sm tracking-wider uppercase hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
            >
              Call: (317) 701-2768
            </a>
            <button 
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => {
                  window.REACT_APP_NAVIGATE('/');
                  setTimeout(() => {
                    const contactEl = document.getElementById('contact');
                    if (contactEl) contactEl.scrollIntoView({ behavior: 'smooth' });
                  }, 100);
                }, 300);
              }}
              className="border-2 border-white text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-white hover:text-gray-900 transition-all cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif", letterSpacing: '0.1em' }}
            >
              Online Contact Form
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
